import React, { useState, useEffect } from "react";
import { Card, Row, Col, Select, Button, Table, Spin, Space, Tooltip, theme, message, Alert } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { fetchTotaisStatus, fetchTotaisTribunalStatus, fetchLista } from "../../services/solucionareService";
import { exportSolucionareToXlsx, mapRowToExport } from "../../utils/exportSolucionare";
import PageHeader from "../../components/common/PageHeader";

const { Option } = Select;

const TRIBUNAIS = ["TJES", "TJPI", "TJTO", "TJRN", "NULL"];
const TRIBUNAIS_MAP: Record<string, string[]> = {
  TJES: [
    "JUS_TJES_PJE",
    "TJES_PENAL",
    "TJES_PROJUDI",
    "TJES_JUIZADO_ESPECIAL",
    "TJES_UNIFICADO",
    "TJES_PJE",
    "TJES_JUSTICA_COMUM",
    "TJES_SEEU",
  ],
  TJPI: ["JUS_TJPI", "TJPI_THEMIS", "TJPI_PJE1", "TJPI_PROJUDI"],
  TJTO: ["TJTO_EPROC1_LGD", "TJTO_EPROC_GOV1", "TJTO_SEEU"],
  TJRN: ["TJRN_ESAJ1", "TJRN_PJE1", "TJRN_PROJUDI"],
};

const STATUS_OPTS = ["VALIDANDO", "CADASTRADO", "ARQUIVADO", "SEGREDO", "ERRO1", "ERRO2", "NULL"];
const INSTANCIA_OPTS = ["1", "2"];

const getApiErrorMessage = (e: unknown, fallback: string): string => {
  if (e instanceof Error && e.message) return e.message;
  const axiosData = (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
  return axiosData?.message || axiosData?.error || fallback;
};

const IntegracoesSolucionareContent: React.FC = () => {
  const { token } = theme.useToken();

  const STATUS_COLOR_MAP: Record<string, string> = {
    VALIDANDO: "#2563eb",
    CADASTRADO: "#16a34a",
    ARQUIVADO: "#64748b",
    SEGREDO: "#7c3aed",
    ERRO1: "#0ea5e9",
    ERRO2: "#ef4444",
    NULL: "#94a3b8",
  };

  const getStatusColor = (s?: string) => {
    if (!s) return STATUS_COLOR_MAP.NULL;
    return STATUS_COLOR_MAP[String(s).toUpperCase()] ?? STATUS_COLOR_MAP.NULL;
  };

  // Ordena alfabeticamente, mantendo "NULL" sempre por último
  const sortWithNullLast = (arr: string[]) => {
    const hasNull = arr.includes("NULL");
    const items = arr.filter((a) => a !== "NULL").slice().sort((a, b) => a.localeCompare(b));
    if (hasNull) items.push("NULL");
    return items;
  };

  const sortedTribunais = sortWithNullLast(TRIBUNAIS);
  const sortedStatusOpts = sortWithNullLast(STATUS_OPTS);

  const [tribunal, setTribunal] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [instancia, setInstancia] = useState<string | undefined>(undefined);
  const [cnj, setCnj] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [totaisStatus, setTotaisStatus] = useState<any[]>([]);
  const [totaisTribunalStatus, setTotaisTribunalStatus] = useState<any[]>([]);

  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [total, setTotal] = useState(0);

  const loadTotals = async () => {
    try {
      const res1 = await fetchTotaisStatus(undefined);
      if (!res1?.success) {
        throw new Error(res1?.message || "Erro ao buscar totais por status");
      }
      setTotaisStatus(res1.data || []);

      const res2 = await fetchTotaisTribunalStatus(undefined);
      if (!res2?.success) {
        throw new Error(res2?.message || "Erro ao buscar totais por tribunal e status");
      }
      // Agregar (somar) usando TRIBUNAIS_MAP: mapear sistemas específicos para seus tribunais
      const raw = res2.data || [];
      // cria mapa reverso: sistema -> tribunal pai
      const reverseMap: Record<string, string> = {};
      Object.entries(TRIBUNAIS_MAP).forEach(([parent, arr]) => {
        arr.forEach((s) => {
          reverseMap[s] = parent;
        });
      });

      // agrega por tribunal pai + status
      const aggregatedMap = new Map<string, number>();
      raw.forEach((row: any) => {
        const sigla = row.siglaSistema ?? "NULL";
        const statusVal = row.status ?? "NULL";
        const parent = reverseMap[sigla] ?? sigla;
        const key = `${parent}::${statusVal}`;
        const prev = aggregatedMap.get(key) || 0;
        aggregatedMap.set(key, prev + (Number(row.total) || 0));
      });

      const aggregated = Array.from(aggregatedMap.entries()).map(([k, v]) => {
        const [parent, statusVal] = k.split("::");
        return { siglaSistema: parent, status: statusVal, total: v };
      });

      setTotaisTribunalStatus(aggregated);
      setErrorMessage(null);
    } catch (e: unknown) {
      const msg = getApiErrorMessage(
        e,
        "Erro ao carregar totais da Solucionare. Verifique se o backend está rodando em http://localhost:3001.",
      );
      setErrorMessage(msg);
      message.error(msg);
    }
  };

  useEffect(() => {
    loadTotals();
    handleFilter(1);
  }, []);

  // Usa símbolo especial para indicar "limpo" vs "sem override" no handleFilter
  const CLEAR_SENTINEL = "__CLEAR__";

  const handleClear = () => {
    setTribunal(undefined);
    setStatus(undefined);
    setInstancia(undefined);
    setCnj([]);
    handleFilter(1, CLEAR_SENTINEL, CLEAR_SENTINEL, CLEAR_SENTINEL, CLEAR_SENTINEL);
  };

  const resolveFilterParams = (
    tribunalOverride?: string,
    statusOverride?: string,
    cnjOverride?: string,
    instanciaOverride?: string,
  ) => {
    const resolvedTribunal = tribunalOverride === CLEAR_SENTINEL ? undefined : tribunalOverride !== undefined ? tribunalOverride : tribunal;
    const tribunalValRaw = resolvedTribunal === "Tribunal" ? undefined : resolvedTribunal;

    let tribunalParam: string | undefined;
    if (!tribunalValRaw) {
      tribunalParam = undefined;
    } else if (tribunalValRaw === "NULL") {
      tribunalParam = "NULL";
    } else if (TRIBUNAIS_MAP[tribunalValRaw]) {
      tribunalParam = TRIBUNAIS_MAP[tribunalValRaw].join(",");
    } else {
      tribunalParam = tribunalValRaw;
    }

    const resolvedStatus = statusOverride === CLEAR_SENTINEL ? undefined : statusOverride !== undefined ? statusOverride : status;
    const statusVal = resolvedStatus === "Status" ? undefined : resolvedStatus;

    const resolvedCnj = cnjOverride === CLEAR_SENTINEL
      ? []
      : cnjOverride !== undefined
        ? cnjOverride.split(",").map((s) => s.trim()).filter(Boolean)
        : cnj.flatMap((c) => c.split(",").map((s) => s.trim()).filter(Boolean));
    const cnjVal = resolvedCnj.length > 0 ? resolvedCnj.join(",") : undefined;

    const resolvedInstancia = instanciaOverride === CLEAR_SENTINEL ? undefined : instanciaOverride !== undefined ? instanciaOverride : instancia;
    const instanciaVal = resolvedInstancia === "Instância" ? undefined : resolvedInstancia;

    return { tribunalParam, statusVal, cnjVal, instanciaVal };
  };

  // Aceita overrides diretos para evitar necessidade de duplo clique (estado React é assíncrono)
  const handleFilter = async (
    newPage = 1,
    tribunalOverride?: string,
    statusOverride?: string,
    cnjOverride?: string,
    instanciaOverride?: string,
  ) => {
    setLoading(true);
    setData([]);
    setTotal(0);

    try {
      const { tribunalParam, statusVal, cnjVal, instanciaVal } = resolveFilterParams(
        tribunalOverride,
        statusOverride,
        cnjOverride,
        instanciaOverride,
      );

      const resp = await fetchLista({
        tribunal: tribunalParam,
        status: statusVal,
        cnj: cnjVal,
        instancia: instanciaVal,
        page: newPage,
        pageSize,
      });

      if (resp && resp.success) {
        // Gera uma chave estável por linha para evitar colisões entre processos com o mesmo CNJ.
        const rows = (resp.data || []).map((row: any, index: number) => ({
          ...row,
          uiKey: [
            row.cnj ?? "NULL",
            row.siglaSistema ?? "NULL",
            row.instancia ?? "NULL",
            row.createdAt ?? "NULL",
            row.updatedAt ?? "NULL",
            row.dataUltimaConsultaProcesso ?? "NULL",
            index,
          ].join("::"),
        }));

        setData(rows);
        setTotal(resp.total || 0);
        setPage(resp.page || newPage);
        setErrorMessage(null);
      } else {
        const msg = resp?.message || resp?.error || "Erro ao buscar lista de processos.";
        setErrorMessage(msg);
        message.error(msg);
      }
    } catch (e: unknown) {
      const msg = getApiErrorMessage(
        e,
        "Erro ao buscar lista de processos. Verifique se o backend está rodando em http://localhost:3001.",
      );
      setErrorMessage(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (total === 0) {
      message.warning("Nenhum registro para exportar. Aplique um filtro ou aguarde o carregamento.");
      return;
    }

    setExporting(true);
    try {
      const { tribunalParam, statusVal, cnjVal, instanciaVal } = resolveFilterParams();

      const resp = await fetchLista({
        tribunal: tribunalParam,
        status: statusVal,
        cnj: cnjVal,
        instancia: instanciaVal,
        page: 1,
        pageSize: total,
      });

      if (resp && resp.success) {
        const rows = (resp.data || []).map((row: Record<string, unknown>) => mapRowToExport(row));
        exportSolucionareToXlsx(rows);
        message.success("Arquivo exportado com sucesso!");
      } else {
        message.error("Erro ao buscar dados para exportação.");
      }
    } catch {
      message.error("Erro ao gerar o arquivo de exportação.");
    } finally {
      setExporting(false);
    }
  };

  const getCountValue = (record: any, key: string) => {
    if (!record) return 0;

    const variants = [
      key,
      key.toLowerCase(),
      `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    ];
    const sources = [
      record,
      record.info,
      record.infoCounts,
      record.counts,
      record.data?.info,
      record.data?.counts,
    ];

    for (const source of sources) {
      if (!source) {
        continue;
      }

      for (const variant of variants) {
        const candidate = source[variant];
        if (candidate === undefined || candidate === null) {
          continue;
        }

        const normalized =
          typeof candidate === "number" ? candidate : Number(candidate);
        if (!Number.isNaN(normalized)) {
          return normalized;
        }
      }
    }

    return 0;
  };

  const columns = [
    { title: "CNJ", dataIndex: "cnj", key: "cnj" },
    {
      title: "codProcesso",
      key: "codProcesso",
      render: (_: any, record: any) =>
        record?.codProcesso ?? record?.DadosProcessos?.codProcesso ?? record?.dp?.codProcesso ?? "-",
    },
    { title: "Status", dataIndex: "status", key: "status" },

    // Coluna adicionada: Descrição (vinda de DadosProcessos.dp.descricaoClassificacaoStatus)
    {
      title: "Descrição",
      key: "descricaoClassificacaoStatus",
      width: 360,
      ellipsis: true,
      render: (_: any, record: any) => {
        // Tentativa em múltiplas fontes do payload para ser resiliente.
        // Prioriza campo no nível superior (ex: retorno do backend já mapeado).
        const val =
          record?.descricaoClassificacaoStatus ??
          record?.DadosProcessos?.descricaoClassificacaoStatus ??
          record?.dp?.descricaoClassificacaoStatus ??
          "-";
        return <span title={val}>{val}</span>;
      },
    },

    { title: "Tribunal", dataIndex: "siglaSistema", key: "siglaSistema" },
    {
      title: "Instância",
      dataIndex: "instancia",
      key: "instancia",
      render: (_: any, record: any) => record?.instancia ?? "-",
    },
    {
      title: "Capas",
      dataIndex: "capas",
      key: "capas",
      render: (_: any, record: any) => getCountValue(record, "capas"),
    },
    {
      title: "Partes",
      dataIndex: "partes",
      key: "partes",
      render: (_: any, record: any) => getCountValue(record, "partes"),
    },
    {
      title: "Andamentos",
      dataIndex: "andamentos",
      key: "andamentos",
      render: (_: any, record: any) => getCountValue(record, "andamentos"),
    },
    {
      title: "Criado",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (_: any, record: any) => {
        const val = record.createdAt || record?.createdAt;
        return val ? new Date(val).toLocaleString() : "-";
      },
    },
    {
      title: "Atualizado",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 160,
      render: (_: any, record: any) => {
        const val = record.updatedAt || record?.updatedAt;
        return val ? new Date(val).toLocaleString() : "-";
      },
    },
    { title: "Última consulta", dataIndex: "dataUltimaConsultaProcesso", key: "dataUltimaConsultaProcesso" },
    { title: "Deletado", dataIndex: "deletedAt", key: "deletedAt" },
  ];

  const legendGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "160px 1fr",
    gap: "8px 12px",
    alignItems: "start",
  };

  const legendLabelStyle: React.CSSProperties = {
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: token.colorText,
  };

  const legendDescStyle: React.CSSProperties = {
    color: token.colorTextSecondary,
  };

  const legendItems = [
    ["VALIDANDO", "Processo recebido pela Solucionare, em fase de coleta de informações."],
    ["CADASTRADO", "Informações encontradas; Codilo já pode consumi-las."],
    ["ARQUIVADO", "Processo arquivado pelo tribunal."],
    ["SEGREDO", "Processo em segredo de justiça."],
    ["ERRO1", "Processo não encontrado no sistema do tribunal."],
    ["ERRO2", "Falha retornada pela Solucionare (outros)."],
    ["NULL", "Status indefinido."],
  ] as [string, string][];

  const dateItems = [
    ["Criado", "Quando cadastramos no nosso banco e enviamos para Solucionare."],
    ["Atualizado", "Quando nós recebemos atualização de status e buscamos as informações iniciais. A rotina interna fica buscando atualizações."],
    ["Última consulta", "Data de quando mudou para o status final: cadastrando. Esta data não será atualizada novamente, mesmo com novas movimentações."],
  ] as [string, string][];
 
  // Dados para matriz: linhas = tribunais, colunas = status
  const matrizTribunais = sortedTribunais;
  const matrizStatus = sortedStatusOpts;

  const getTotalFor = (trib: string, stat: string) =>
    (totaisTribunalStatus || [])
      .filter((r: any) => (r.siglaSistema ?? "NULL") === trib && (r.status ?? "NULL") === stat)
      .reduce((acc: number, r: any) => acc + (Number(r.total) || 0), 0);

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <PageHeader title="Integrações Solucionare" />

        {errorMessage && (
          <Alert
            type="error"
            showIcon
            message="Erro ao carregar dados"
            description={errorMessage}
            closable
            onClose={() => setErrorMessage(null)}
          />
        )}

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title="Totais por status">
              <Row gutter={[8, 8]}>
                {totaisStatus.map((t: any) => {
                  const statusKey = t.status ?? "NULL";
                  const color = getStatusColor(statusKey);
                  return (
                    <Col key={String(statusKey)} span={8}>
                      <Card>
                        <div style={{ fontSize: 18, fontWeight: 600, color }}>{t.total}</div>
                        <div style={{ color }}>{statusKey}</div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="Tribunais x status">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "center" }}>
                  <thead>
                    <tr>
                      <th style={{ borderBottom: `1px solid ${token.colorBorder}`, padding: 8, textAlign: "left" }}>
                        Tribunal \ Status
                      </th>
                      {matrizStatus.map((s) => (
                        <th key={s} style={{ borderBottom: `1px solid ${token.colorBorder}`, padding: 8, color: getStatusColor(s) }}>
                          {s}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrizTribunais.map((t) => (
                      <tr key={t}>
                        <td style={{ padding: 8, fontWeight: 700, textAlign: "left" }}>{t}</td>
                        {matrizStatus.map((s) => (
                          <td
                            key={s}
                            style={{
                              padding: 8,
                              borderLeft: `1px solid ${token.colorBorder}`,
                              color: getStatusColor(s),
                            }}
                          >
                            {getTotalFor(t, s)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </Col>

          <Col span={24}>
            <Row
              gutter={[24, 0]}
              style={{
                padding: 16,
                borderRadius: token.borderRadiusLG,
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
              }}
            >
              <Col span={12}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: token.colorText }}>Status</div>
                <div style={legendGridStyle}>
                  {legendItems.map(([k, v]) => (
                    <React.Fragment key={k}>
                      <div style={{ ...legendLabelStyle, color: getStatusColor(k) }}>{k}:</div>
                      <div style={legendDescStyle}>{v}</div>
                    </React.Fragment>
                  ))}
                </div>
              </Col>
              <Col span={12} style={{ borderLeft: `1px solid ${token.colorBorder}`, paddingLeft: 24 }}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: token.colorText }}>Datas</div>
                <div style={legendGridStyle}>
                  {dateItems.map(([k, v]) => (
                    <React.Fragment key={k}>
                      <div style={legendLabelStyle}>{k}:</div>
                      <div style={legendDescStyle}>{v}</div>
                    </React.Fragment>
                  ))}
                </div>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Card title="Processos">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <Space wrap>
                  <Select
                    allowClear
                    placeholder="Tribunal"
                    style={{ width: 180 }}
                    value={tribunal}
                    onChange={(v) => setTribunal(v)}
                  >
                    {sortedTribunais.map((t) => (
                      <Option key={t} value={t}>
                        {t}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    allowClear
                    placeholder="Status"
                    style={{ width: 200 }}
                    value={status}
                    onChange={(v) => setStatus(v)}
                  >
                    {sortedStatusOpts.map((s) => (
                      <Option key={s} value={s}>
                        <Tooltip title="VALIDANDO, CADASTRADO, ARQUIVADO, SEGREDO, ERRO1, ERRO2, NULL">
                          {s}
                        </Tooltip>
                      </Option>
                    ))}
                  </Select>

                  <Select
                    allowClear
                    placeholder="Instância"
                    style={{ width: 120 }}
                    value={instancia}
                    onChange={(v) => setInstancia(v)}
                  >
                    {INSTANCIA_OPTS.map((i) => (
                      <Option key={i} value={i}>
                        {i}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    mode="tags"
                    placeholder="CNJ (um ou mais)"
                    style={{ width: 320 }}
                    value={cnj}
                    onChange={(v) => setCnj(v)}
                    tokenSeparators={[","]}
                  />

                  <Button type="primary" onClick={() => handleFilter(1)}>
                    Filtrar
                  </Button>

                  <Button onClick={handleClear}>
                    Limpar
                  </Button>
                </Space>

                <Space align="center">
                  {!loading && (
                    <span style={{ color: token.colorTextSecondary }}>
                      {total === 0
                        ? "0-0 de 0 registros"
                        : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)} de ${total} registros`}
                    </span>
                  )}
                  <Button
                    icon={<DownloadOutlined />}
                    loading={exporting}
                    onClick={handleExport}
                    disabled={loading || total === 0}
                  >
                    Exportar Excel
                  </Button>
                </Space>
              </div>

              {loading ? (
                <div style={{ padding: 40, textAlign: "center" }}>
                  <div>Aguardando...</div>
                  <Spin style={{ marginTop: 12 }} />
                </div>
              ) : (
                <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table
                    dataSource={data}
                    columns={columns.map((col) => {
                      if (col.dataIndex === "status" || col.dataIndex === "siglaSistema") {
                        return {
                          ...col,
                          render: (v: any) => v ?? "NULL",
                        };
                      }
                      return col;
                    })}
                    rowKey="uiKey"
                    pagination={{
                      current: page,
                      pageSize,
                      total,
                      onChange: (p) => handleFilter(p),
                    }}
                    size="small"
                    scroll={{ x: "max-content" }}
                    style={{ minWidth: "100%" }}
                  />
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

const IntegracoesSolucionare: React.FC = () => {
  return (
    <IntegracoesSolucionareContent />
  );
};

export default IntegracoesSolucionare;